/**
 * The storefront's reads of paio-ordering (ORDR-118; FRS-31, FRS-36, FRS-41).
 *
 * Four public endpoints, verified on paio-ordering main `e42b85a`:
 *
 *   #2  hours/<location>/?mode=           is this location taking orders now
 *   #3  locations/<location>/windows/     the declared windows of a WINDOWED mode
 *   #4  locations/<location>/schedule/    the published hours
 *   #5  locations/<location>/readiness/   the location's readiness ESTIMATE
 *
 * #6, an order's quoted ready time, was read here until ORDR-166 replaced it
 * with the diner status read (`../status/statusApi.ts`, ORDR-162), which
 * carries the kitchen's stage as well as the times.
 *
 * A RESULT, NEVER A THROW, AND NEVER A PARTIAL OBJECT. Every field is mapped
 * explicitly. A 200 whose body lacks a documented key, or carries it with the
 * wrong type, is `failed` with a `console.warn` naming the key: a parser that
 * skipped it would render "nothing to show", which is the defect, not the
 * fallback.
 *
 * OPEN VOCABULARIES. `reason`, `state` and mode keys are kept
 * as the strings the server sent. Nothing here narrows them to a known list,
 * so an unknown value reaches the renderer, which takes the generic path.
 */

import type { ApiClient } from "../api/client.js";

export type StorefrontRead<K extends string, T> =
  | ({ readonly kind: "ok" } & { readonly [key in K]: T })
  | { readonly kind: "not-found" }
  | { readonly kind: "failed"; readonly correlationId: string };

export interface NextWindow {
  readonly window_id: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly shift_key: string | null;
  readonly label: string | null;
  readonly remaining_units: number | null;
}

export interface Hours {
  readonly site_id: string;
  readonly timezone: string;
  readonly mode: string;
  readonly at: string;
  readonly local_time: string;
  readonly is_open: boolean;
  readonly accepts_orders: boolean;
  readonly reason: string | null;
  readonly closure_reason: string | null;
  readonly next_opens_at: string | null;
  readonly opens_at: string | null;
  readonly closes_at: string | null;
  readonly last_order_at: string | null;
  readonly temporal_shape: string;
  readonly next_window: NextWindow | null;
}

export interface OrderWindow {
  readonly window_id: string;
  readonly shift_key: string | null;
  readonly label: string | null;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly last_order_at: string | null;
  readonly capacity_limited: boolean;
  readonly unit: string | null;
  readonly remaining_units: number | null;
}

export interface Windows {
  readonly location_node_id: string;
  readonly mode: string;
  readonly at: string;
  readonly temporal_shape: string;
  readonly horizon_days: number;
  readonly windows: readonly OrderWindow[];
  readonly refusal: { readonly reason: string } | null;
}

export interface Shift {
  /** 0 is Monday: the day the shift OPENS. */
  readonly day_of_week: number;
  readonly opens: string;
  readonly closes: string;
  readonly crosses_midnight: boolean;
  readonly shift_key: string | null;
  readonly label: string | null;
}

export interface ModeSchedule {
  readonly mode: string;
  readonly temporal_shape: string;
  readonly shifts: readonly Shift[];
}

export interface ScheduleException {
  readonly date_from: string;
  readonly date_to: string;
  readonly mode: string | null;
  readonly shift_key: string | null;
  readonly closed: boolean;
  readonly opens: string | null;
  readonly closes: string | null;
  readonly reason: string | null;
}

export interface Schedule {
  readonly location_node_id: string;
  readonly timezone: string;
  readonly modes: readonly ModeSchedule[];
  readonly exceptions: readonly ScheduleException[];
}

export interface Readiness {
  readonly location_node_id: string;
  readonly mode: string | null;
  readonly at: string;
  readonly state: string;
  readonly reason: string | null;
  readonly ready_at: string | null;
  readonly ready_in_seconds: number | null;
  readonly capacity_coefficient_permille: number | null;
  readonly basis: string;
}

/**
 * Reads one record's fields and remembers the FIRST that is missing or of the
 * wrong type, by its dotted path. Accessors return a placeholder after a
 * fault; the caller checks `fault` and discards the whole object.
 */
class FieldReader {
  fault: string | null = null;
  private readonly source: Record<string, unknown>;
  private readonly path: string;

  constructor(source: Record<string, unknown>, path: string) {
    this.source = source;
    this.path = path;
  }

  private take(key: string, accept: (value: unknown) => boolean): unknown {
    const present = Object.prototype.hasOwnProperty.call(this.source, key);
    const value = this.source[key];
    if (!present || !accept(value)) {
      this.fault ??= `${this.path}${key}`;
      return undefined;
    }
    return value;
  }

  string(key: string): string {
    return (this.take(key, (value) => typeof value === "string") as string | undefined) ?? "";
  }

  stringOrNull(key: string): string | null {
    return (this.take(key, (value) => value === null || typeof value === "string") as string | null | undefined) ?? null;
  }

  boolean(key: string): boolean {
    return (this.take(key, (value) => typeof value === "boolean") as boolean | undefined) ?? false;
  }

  number(key: string): number {
    return (this.take(key, (value) => typeof value === "number") as number | undefined) ?? 0;
  }

  numberOrNull(key: string): number | null {
    return (this.take(key, (value) => value === null || typeof value === "number") as number | null | undefined) ?? null;
  }

  /** A nested record, or `null` when the server sent `null` and `nullable`. */
  record(key: string, nullable: boolean): FieldReader | null {
    const value = this.take(
      key,
      (candidate) => (nullable && candidate === null) || (typeof candidate === "object" && candidate !== null && !Array.isArray(candidate)),
    );
    if (value === undefined || value === null) {
      return null;
    }
    return this.child(value as Record<string, unknown>, `${key}.`);
  }

  list(key: string): FieldReader[] {
    const value = this.take(key, (candidate) => Array.isArray(candidate));
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((entry, index) => {
      if (typeof entry !== "object" || entry === null) {
        this.fault ??= `${this.path}${key}[${index}]`;
        return this.child({}, `${key}[${index}].`);
      }
      return this.child(entry as Record<string, unknown>, `${key}[${index}].`);
    });
  }

  /** Folds a child's fault into this reader, so one check covers the tree. */
  adopt<T>(child: FieldReader, value: T): T {
    this.fault ??= child.fault;
    return value;
  }

  private child(record: Record<string, unknown>, suffix: string): FieldReader {
    return new FieldReader(record, `${this.path}${suffix}`);
  }
}

async function read<K extends string, T>(
  client: ApiClient,
  path: string,
  key: K,
  parse: (data: FieldReader) => T,
  isNotFound: (status: number, body: unknown) => boolean = (status) => status === 404,
): Promise<StorefrontRead<K, T>> {
  const response = await client.request(path);
  if (response.status === 200) {
    const data = (response.body as { data?: unknown } | null)?.data;
    if (typeof data !== "object" || data === null || Array.isArray(data)) {
      console.warn(`[storefront] ${path}: the 200 body has no data object`, { correlationId: response.correlationId });
      return { kind: "failed", correlationId: response.correlationId };
    }
    const reader = new FieldReader(data as Record<string, unknown>, "data.");
    const value = parse(reader);
    if (reader.fault !== null) {
      console.warn(`[storefront] ${path}: ${reader.fault} is missing or not the documented type`, {
        correlationId: response.correlationId,
      });
      return { kind: "failed", correlationId: response.correlationId };
    }
    return { kind: "ok", [key]: value } as StorefrontRead<K, T>;
  }
  if (response.status !== null && isNotFound(response.status, response.body)) {
    return { kind: "not-found" };
  }
  return { kind: "failed", correlationId: response.correlationId };
}

function modeQuery(mode: string | null): string {
  return mode === null ? "" : `?${new URLSearchParams({ mode }).toString()}`;
}

/** #2. `mode` is REQUIRED by the server: a key the flow resolver sent. */
export function readHours(client: ApiClient, locationNodeId: string, mode: string): Promise<StorefrontRead<"hours", Hours>> {
  return read(client, `/ordering/hours/${locationNodeId}/${modeQuery(mode)}`, "hours", (data) => {
    const next = data.record("next_window", true);
    const nextWindow =
      next === null
        ? null
        : data.adopt(next, {
            window_id: next.string("window_id"),
            starts_at: next.string("starts_at"),
            ends_at: next.string("ends_at"),
            shift_key: next.stringOrNull("shift_key"),
            label: next.stringOrNull("label"),
            remaining_units: next.numberOrNull("remaining_units"),
          });
    return {
      site_id: data.string("site_id"),
      timezone: data.string("timezone"),
      mode: data.string("mode"),
      at: data.string("at"),
      local_time: data.string("local_time"),
      is_open: data.boolean("is_open"),
      accepts_orders: data.boolean("accepts_orders"),
      reason: data.stringOrNull("reason"),
      closure_reason: data.stringOrNull("closure_reason"),
      next_opens_at: data.stringOrNull("next_opens_at"),
      opens_at: data.stringOrNull("opens_at"),
      closes_at: data.stringOrNull("closes_at"),
      last_order_at: data.stringOrNull("last_order_at"),
      temporal_shape: data.string("temporal_shape"),
      next_window: nextWindow,
    };
  });
}

/** #3. Only meaningful for a WINDOWED mode; the caller decides when to ask. */
export function readWindows(client: ApiClient, locationNodeId: string, mode: string): Promise<StorefrontRead<"windows", Windows>> {
  return read(client, `/ordering/locations/${locationNodeId}/windows/${modeQuery(mode)}`, "windows", (data) => {
    const windows = data.list("windows").map((entry) =>
      data.adopt(entry, {
        window_id: entry.string("window_id"),
        shift_key: entry.stringOrNull("shift_key"),
        label: entry.stringOrNull("label"),
        starts_at: entry.string("starts_at"),
        ends_at: entry.string("ends_at"),
        last_order_at: entry.stringOrNull("last_order_at"),
        capacity_limited: entry.boolean("capacity_limited"),
        unit: entry.stringOrNull("unit"),
        remaining_units: entry.numberOrNull("remaining_units"),
      }),
    );
    const refused = data.record("refusal", true);
    return {
      location_node_id: data.string("location_node_id"),
      mode: data.string("mode"),
      at: data.string("at"),
      temporal_shape: data.string("temporal_shape"),
      horizon_days: data.number("horizon_days"),
      windows,
      refusal: refused === null ? null : data.adopt(refused, { reason: refused.string("reason") }),
    };
  });
}

/** #4. Read once per location, for every mode, and filtered by the caller. */
export function readSchedule(client: ApiClient, locationNodeId: string): Promise<StorefrontRead<"schedule", Schedule>> {
  return read(client, `/ordering/locations/${locationNodeId}/schedule/`, "schedule", (data) => ({
    location_node_id: data.string("location_node_id"),
    timezone: data.string("timezone"),
    modes: data.list("modes").map((mode) =>
      data.adopt(mode, {
        mode: mode.string("mode"),
        temporal_shape: mode.string("temporal_shape"),
        shifts: mode.list("shifts").map((shift) =>
          mode.adopt(shift, {
            day_of_week: shift.number("day_of_week"),
            opens: shift.string("opens"),
            closes: shift.string("closes"),
            crosses_midnight: shift.boolean("crosses_midnight"),
            shift_key: shift.stringOrNull("shift_key"),
            label: shift.stringOrNull("label"),
          }),
        ),
      }),
    ),
    exceptions: data.list("exceptions").map((exception) =>
      data.adopt(exception, {
        date_from: exception.string("date_from"),
        date_to: exception.string("date_to"),
        mode: exception.stringOrNull("mode"),
        shift_key: exception.stringOrNull("shift_key"),
        closed: exception.boolean("closed"),
        opens: exception.stringOrNull("opens"),
        closes: exception.stringOrNull("closes"),
        reason: exception.stringOrNull("reason"),
      }),
    ),
  }));
}

/** #5. Always "now": the endpoint takes no `at`. */
export function readReadiness(
  client: ApiClient,
  locationNodeId: string,
  mode: string | null,
): Promise<StorefrontRead<"readiness", Readiness>> {
  return read(client, `/ordering/locations/${locationNodeId}/readiness/${modeQuery(mode)}`, "readiness", (data) => ({
    location_node_id: data.string("location_node_id"),
    mode: data.stringOrNull("mode"),
    at: data.string("at"),
    state: data.string("state"),
    reason: data.stringOrNull("reason"),
    ready_at: data.stringOrNull("ready_at"),
    ready_in_seconds: data.numberOrNull("ready_in_seconds"),
    capacity_coefficient_permille: data.numberOrNull("capacity_coefficient_permille"),
    basis: data.string("basis"),
  }));
}
